import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	View,
	useWindowDimensions,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import {
	useLazyQuery,
	useMutation,
} from '@apollo/client';
import Toast from 'react-native-root-toast';

import { CREATE_RETURNED_PENDING_STOCK_FROM_PRODUCT } from '../../../common/graphql/pendingStock';
import { GET_PRODUCT_FROM_BARCODE } from '../../../common/graphql/product';
import { useUserContext } from '../../../common/utils/context';
import createClientOperationId from '../../../common/utils/createClientOperationId';
import { formatNumber, valueExists } from '../../../common/utils/utils';
import {
	normalizeCalendarDateKey,
	serializeDateKeyForTimezone,
} from '../../../common/utils/warehousing/formatExpiryBatchSummary';

import {
	Button,
	Dialog,
	Picker,
} from '../../components';
import { DateSelectorInput } from '../../components/input';
import { getUnitsLabel } from '../../utils/unitsLabel';
import StocktakeQuantitySelector from '../../components/stocktake/StocktakeQuantitySelector';
import Text from '../../components/text/Text';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import { normalizeQuantity } from './PendingStockHelpers';
import PendingStockScannerView from './PendingStockScannerView';
import useReturnServiceRuns from './useReturnServiceRuns';
import { createPendingStockCreateReturnDialogStyles } from './PendingStockCreateReturnDialog.styles';

const DEFAULT_TIMEZONE = 'Australia/Melbourne';

const buildBatchRow = quantity => ({
	quantity: quantity || null,
	expiryDate: '',
});

const DIALOG_MAX_HEIGHT_RATIO = 0.75;

const PendingStockCreateReturnDialog = ({
	isOpen,
	runDate,
	onClose,
	onCreated,
}) => {
	const styles = useThemedStyles(createPendingStockCreateReturnDialogStyles);
	const { height: windowHeight } = useWindowDimensions();
	const { tenant } = useUserContext();
	const timezone = tenant?.timezone || DEFAULT_TIMEZONE;
	const [selectedServiceRunId, setSelectedServiceRunId] = useState(null);
	const [showScanner, setShowScanner] = useState(false);
	const [scannedBarcode, setScannedBarcode] = useState('');
	const [scannedProduct, setScannedProduct] = useState(null);
	const [batchRows, setBatchRows] = useState(() => [buildBatchRow(1)]);
	const creationOperationRef = useRef(null);

	const {
		plannedDateRange,
		serviceRuns,
		serviceRunOptions,
		serviceRunsLoading,
		serviceRunsError,
	} = useReturnServiceRuns({ isOpen, runDate, timezone });

	const [
		getProductFromBarcode,
		{ loading: productLookupLoading },
	] = useLazyQuery(GET_PRODUCT_FROM_BARCODE, {
		fetchPolicy: 'network-only',
	});
	const [
		createReturnedPendingStockFromProduct,
		{ loading: createLoading },
	] = useMutation(CREATE_RETURNED_PENDING_STOCK_FROM_PRODUCT);

	const selectedServiceRun = serviceRuns.find(serviceRun => serviceRun.id === selectedServiceRunId) || null;
	const returnWarehouse = selectedServiceRun?.returnWarehouse || selectedServiceRun?.warehouse || null;
	const normalizedBatchRows = useMemo(() => (
		batchRows.map(row => ({
			quantity: normalizeQuantity(row.quantity),
			expiryDate: normalizeCalendarDateKey(row.expiryDate) || '',
		}))
	), [batchRows]);
	const totalQuantity = useMemo(() => (
		normalizedBatchRows.reduce((total, row) => total + row.quantity, 0)
	), [normalizedBatchRows]);
	const areBatchRowsComplete = Boolean(normalizedBatchRows.length) &&
		normalizedBatchRows.every(row => row.quantity > 0 && row.expiryDate);
	const canSubmit = Boolean(selectedServiceRun?.id &&
		returnWarehouse?.id &&
		scannedProduct?.id &&
		totalQuantity > 0 &&
		areBatchRowsComplete);
	const warehouseProduct = useMemo(() => ({
		product: scannedProduct,
	}), [scannedProduct]);

	const handleProductBarcodeScanned = useCallback(async (barcode) => {
		setScannedBarcode(barcode);

		try {
			const response = await getProductFromBarcode({
				variables: {
					barcode,
				},
			});
			const product = response?.data?.getProductFromBarcode;
			if (!product?.id) {
				Toast.show('No product found for scanned barcode');
				return;
			}

			setScannedProduct(product);
			setShowScanner(false);
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to look up scanned product');
		}
	}, [getProductFromBarcode]);

	const {
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		resetScanner,
		isScanning,
	} = useBarcodeScanner({
		onScanned: handleProductBarcodeScanned,
	});

	const resetProduct = useCallback(() => {
		setScannedBarcode('');
		setScannedProduct(null);
		setBatchRows([buildBatchRow(1)]);
	}, []);

	const resetForm = useCallback(() => {
		creationOperationRef.current = null;
		setSelectedServiceRunId(null);
		setShowScanner(false);
		resetProduct();
		resetScanner();
	}, [resetProduct, resetScanner]);

	useEffect(() => {
		resetForm();
	}, [runDate, resetForm]);

	useEffect(() => {
		if (!isOpen) {
			resetForm();
		}
	}, [isOpen, resetForm]);

	const handleServiceRunChange = useCallback((serviceRunId) => {
		setSelectedServiceRunId(serviceRunId);
		setShowScanner(false);
		resetProduct();
		resetScanner();
	}, [resetProduct, resetScanner]);

	const openScanner = useCallback(() => {
		if (!returnWarehouse?.id) {
			Toast.show('The selected service run does not have a return warehouse');
			return;
		}

		resetProduct();
		resetScanner();
		setShowScanner(true);
		requestCameraPermission();
	}, [requestCameraPermission, resetProduct, resetScanner, returnWarehouse?.id]);

	const updateBatchRow = useCallback((index, field, value) => {
		setBatchRows(previous => previous.map((row, rowIndex) => (
			rowIndex === index ? { ...row, [field]: value } : row
		)));
	}, []);

	const addBatchRow = useCallback(() => {
		setBatchRows(previous => [...previous, buildBatchRow(null)]);
	}, []);

	const removeBatchRow = useCallback((index) => {
		setBatchRows(previous => (
			previous.length > 1
				? previous.filter((_, rowIndex) => rowIndex !== index)
				: previous
		));
	}, []);

	const submitReturnedStock = useCallback(async () => {
		if (!canSubmit) {
			return;
		}

		const batchExpiryDates = {};
		let unitIndex = 1;
		normalizedBatchRows.forEach(row => {
			for (let i = 0; i < row.quantity; i += 1) {
				batchExpiryDates[unitIndex] = serializeDateKeyForTimezone(row.expiryDate, timezone);
				unitIndex += 1;
			}
		});
		const earliestExpiryDate = normalizedBatchRows.reduce((earliestDate, row) => (
			!earliestDate || row.expiryDate < earliestDate ? row.expiryDate : earliestDate
		), null);
		const requestVariables = {
			warehouseId: returnWarehouse.id,
			productId: scannedProduct.id,
			serviceRunId: selectedServiceRun.id,
			quantity: totalQuantity,
			expiryDate: serializeDateKeyForTimezone(earliestExpiryDate, timezone),
			batchExpiryDates,
			note: scannedBarcode
				? `Excess returned stock scanned from barcode ${scannedBarcode}`
				: 'Excess returned stock scanned by picker',
		};
		const requestKey = JSON.stringify(requestVariables);
		if (creationOperationRef.current?.requestKey !== requestKey) {
			creationOperationRef.current = {
				requestKey,
				operationId: createClientOperationId('returned-stock'),
			};
		}

		try {
			await createReturnedPendingStockFromProduct({
				variables: {
					...requestVariables,
					operationId: creationOperationRef.current.operationId,
				},
			});
			await onCreated?.();
			Toast.show('Returned stock added');
			onClose();
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to add returned stock');
		}
	}, [
		canSubmit,
		createReturnedPendingStockFromProduct,
		normalizedBatchRows,
		onClose,
		onCreated,
		returnWarehouse?.id,
		scannedBarcode,
		scannedProduct?.id,
		selectedServiceRun?.id,
		timezone,
		totalQuantity,
	]);

	return (
		<Dialog
			isOpen={isOpen}
			onClose={onClose}
			noPadding={showScanner}
		>
			{showScanner ? (
				<PendingStockScannerView
					cameraPermission={cameraPermission}
					requestCameraPermission={requestCameraPermission}
					onBarcodeScanned={isScanning || productLookupLoading ? undefined : handleBarcodeScanned}
					prompt="Scan the returned product."
					isLookingUpProduct={productLookupLoading}
				/>
			) : (
				// A pixel cap (not a percentage of the auto-height dialog) lets the form scroll
				// and keeps the actions visible on short screens and on web.
				<View style={{ maxHeight: windowHeight * DIALOG_MAX_HEIGHT_RATIO }}>
					<KeyboardAwareScrollView
						style={styles.dialogScroll}
						contentContainerStyle={styles.dialogContent}
						enableOnAndroid={true}
						extraScrollHeight={32}
						keyboardShouldPersistTaps="handled"
						showsVerticalScrollIndicator={false}
					>
						<Text style={styles.dialogTitle}>Add returned stock</Text>
						<Text style={styles.dialogMeta}>Run date: {runDate}</Text>
						<Text style={styles.inputLabel}>Service run</Text>
						<Picker
							selectedValue={selectedServiceRunId}
							onValueChange={handleServiceRunChange}
							options={serviceRunOptions}
							emptyValueLabel={serviceRunsLoading ? 'Loading service runs...' : 'Select service run'}
							disabled={serviceRunsLoading || !plannedDateRange}
							width="100%"
						/>
						{serviceRunsError && (
							<Text style={styles.errorText}>Unable to load service runs for this date.</Text>
						)}
						{!serviceRunsLoading && !serviceRunsError && plannedDateRange && serviceRuns.length === 0 && (
							<Text style={styles.warningText}>No service runs were found for this date.</Text>
						)}
						{selectedServiceRun && (
							<>
								<Text style={styles.inputLabel}>Return warehouse</Text>
								<Text style={styles.dialogMeta}>
									{returnWarehouse?.name || 'No return warehouse configured'}
								</Text>
								{!returnWarehouse?.id && (
									<Text style={styles.errorText}>
										Configure a warehouse on this service run before recording returned stock.
									</Text>
								)}
							</>
						)}
						{scannedProduct && (
							<>
								<Text style={styles.inputLabel}>Product</Text>
								<Text style={styles.dialogMeta}>{scannedProduct.name}</Text>
								{scannedBarcode ? (
									<Text style={styles.dialogMeta}>Barcode: {scannedBarcode}</Text>
								) : null}
								<Text style={styles.inputLabel}>Expiry batches</Text>
								{batchRows.map((row, index) => (
									<View key={index} style={styles.batchRow}>
										<Text style={styles.batchHeading}>Batch {index + 1}</Text>
										<StocktakeQuantitySelector
											warehouseProduct={warehouseProduct}
											value={valueExists(row.quantity) ? row.quantity : null}
											onChange={value => updateBatchRow(index, 'quantity', value)}
											disabled={createLoading}
										/>
										<DateSelectorInput
											value={row.expiryDate}
											onChange={value => updateBatchRow(index, 'expiryDate', value)}
											placeholder="Expiry date"
											disabled={createLoading}
											style={styles.batchDate}
										/>
										{batchRows.length > 1 && (
											<Button
												label="Remove batch"
												onPress={() => removeBatchRow(index)}
												disabled={createLoading}
											/>
										)}
									</View>
								))}
								<View style={styles.batchFooter}>
									<Button
										label="Add batch"
										onPress={addBatchRow}
										disabled={createLoading}
									/>
									<Text style={styles.batchTotal}>Total quantity: {formatNumber(totalQuantity)} {getUnitsLabel(totalQuantity)}</Text>
								</View>
								{!areBatchRowsComplete && (
									<Text style={styles.warningText}>
										Enter a positive quantity and expiry date for every batch.
									</Text>
								)}
							</>
						)}
					</KeyboardAwareScrollView>
					<View style={styles.dialogActions}>
						{scannedProduct ? (
							<>
								<Button
									style={styles.dialogActionButton}
									label="Scan again"
									onPress={openScanner}
									disabled={createLoading}
								/>
								<Button
									style={styles.dialogActionButton}
									label="Add stock"
									onPress={submitReturnedStock}
									secondary={true}
									loading={createLoading}
									disabled={!canSubmit || createLoading}
								/>
							</>
						) : (
							<Button
								style={styles.dialogActionButton}
								label="Scan product"
								onPress={openScanner}
								secondary={true}
								disabled={!returnWarehouse?.id || serviceRunsLoading}
							/>
						)}
					</View>
				</View>
			)}
		</Dialog>
	);
};

export default PendingStockCreateReturnDialog;
